import {
	http,
	registerAccessDeniedRecoveryHandler,
	TOKEN_KEY,
} from "@crest/web-common/api/client.js";
import type { SitemapNode } from "@crest/web-common/layout/AppLayout.js";
import type {
	ApplicationAccessTokenResponse,
	MfApplicationId,
	RuntimeApplicationAccessDecision,
	RuntimeApplicationRelease,
} from "@crest/shared-types";
import { MF_APPLICATION_IDS } from "@crest/shared-types";

export type RuntimeMicroApp = RuntimeApplicationRelease;

export interface PlatformRuntimeContext {
	rolloutId: string | null;
	rolloutVersion: string;
	entitlementVersion: string;
	applicationTokenMode: "application" | "platform-session";
	apps: Record<MfApplicationId, RuntimeApplicationAccessDecision>;
	sitemaps: Record<string, SitemapNode[]>;
}

const RUNTIME_CONTEXT_TTL_MS = 15_000;
let cached:
	| {
			identityKey: string;
			expiresAt: number;
			promise: Promise<PlatformRuntimeContext>;
	  }
	| undefined;
let latestContext: PlatformRuntimeContext | undefined;
let forcedRefresh:
	| { identityKey: string; promise: Promise<PlatformRuntimeContext> }
	| undefined;
const listeners = new Set<(context: PlatformRuntimeContext) => void>();
const applicationTokens = new Map<
	MfApplicationId,
	{ token: string; entitlementVersion: string; expiresAt: number }
>();

export function loadPlatformRuntimeContext(options?: {
	force?: boolean;
}): Promise<PlatformRuntimeContext> {
	const identityKey = currentIdentityKey();
	const force = options?.force === true;
	if (force && forcedRefresh?.identityKey === identityKey) return forcedRefresh.promise;
	if (
		!force &&
		cached?.identityKey === identityKey &&
		cached.expiresAt > Date.now()
	) {
		return cached.promise;
	}
	const previousRevision = latestContext ? runtimeContextRevision(latestContext) : undefined;
	const promise = http
		.get<unknown>("/apps/runtime-context", {
			params: { t: Date.now() },
		})
		.then((response) => {
			const context = normalizeRuntimeContext(response.data);
			if (latestContext?.entitlementVersion !== context.entitlementVersion) {
				applicationTokens.clear();
			}
			latestContext = context;
			if (force && previousRevision !== runtimeContextRevision(context)) {
				for (const listener of listeners) listener(context);
			}
			return context;
		})
		.catch((error) => {
			if (cached?.promise === promise) cached = undefined;
			throw error;
		})
		.finally(() => {
			if (forcedRefresh?.promise === promise) forcedRefresh = undefined;
		});
	cached = {
		identityKey,
		expiresAt: Date.now() + RUNTIME_CONTEXT_TTL_MS,
		promise,
	};
	if (force) forcedRefresh = { identityKey, promise };
	return promise;
}

export async function loadPlatformSitemap(
	appName: string,
): Promise<SitemapNode[]> {
	const context = await loadPlatformRuntimeContext();
	return context.sitemaps[appName] ?? [];
}

export function subscribePlatformRuntimeContext(
	listener: (context: PlatformRuntimeContext) => void,
): () => void {
	listeners.add(listener);
	return () => listeners.delete(listener);
}

/** 令牌只缓存在内存中；每个 audience 独立，权限版本变化时立即清空。 */
export async function getApplicationAccessToken(
	appName: MfApplicationId,
	options?: { force?: boolean },
): Promise<string> {
	const force = options?.force === true;
	const context = await loadPlatformRuntimeContext({ force });
	const decision = context.apps[appName];
	if (!decision.allowed) throw new Error(decision.reasonCode ?? 'APP_NOT_ASSIGNED');
	if (context.applicationTokenMode === "platform-session") {
		return currentPlatformSessionToken();
	}
	const current = applicationTokens.get(appName);
	if (
		!force &&
		current &&
		current.entitlementVersion === context.entitlementVersion &&
		current.expiresAt > Date.now() + 30_000
	) {
		return current.token;
	}
	const response = await http.post<ApplicationAccessTokenResponse>(
		`/apps/${appName}/access-token`,
	);
	const issued = response.data;
	if (issued.audience !== appName || issued.entitlementVersion !== context.entitlementVersion) {
		throw new Error('应用令牌与当前访问决策不一致');
	}
	applicationTokens.set(appName, {
		token: issued.access_token,
		entitlementVersion: issued.entitlementVersion,
		expiresAt: Date.now() + issued.expires_in * 1_000,
	});
	return issued.access_token;
}

export function runtimeApplicationForPath(pathname: string): MfApplicationId | undefined {
	const path = pathname.toLowerCase();
	if (path.startsWith('/app/crm')) return 'crm';
	if (path.startsWith('/app/operations') || path.startsWith('/app/reporting')) {
		return 'operations';
	}
	if (
		path.startsWith('/app/agent') ||
		path.startsWith('/app/admin/agent') ||
		path.startsWith('/app/admin/workbench') ||
		path.startsWith('/app/admin/intelligent-approval') ||
		path.startsWith('/app/admin/approval-management') ||
		path.startsWith('/app/admin/process-integration')
	) {
		return 'agent';
	}
	return undefined;
}

export function resetPlatformRuntimeContextForTests() {
	cached = undefined;
	latestContext = undefined;
	forcedRefresh = undefined;
	applicationTokens.clear();
}

function currentIdentityKey(): string {
	if (typeof window === "undefined") return "server";
	return window.localStorage.getItem(TOKEN_KEY) ?? "cookie-session";
}

function currentPlatformSessionToken(): string {
	if (typeof window === "undefined") return "";
	return window.localStorage.getItem(TOKEN_KEY) ?? "";
}

function normalizeRuntimeContext(value: unknown): PlatformRuntimeContext {
	if (!isRecord(value)) throw new Error("Platform runtime-context 格式无效");
	const rolloutVersion =
		typeof value.rolloutVersion === "string" ? value.rolloutVersion : "legacy";
	const nativeEntitlementVersion =
		typeof value.entitlementVersion === "string" && value.entitlementVersion.length > 0
			? value.entitlementVersion
			: undefined;
	const entitlementVersion = nativeEntitlementVersion ?? `legacy:${rolloutVersion}`;
	const rawApps = isRecord(value.apps) ? value.apps : {};
	const apps = Object.fromEntries(
		MF_APPLICATION_IDS.map((appName) => [
			appName,
			normalizeApplicationDecision(appName, rawApps[appName], entitlementVersion),
		]),
	) as Record<MfApplicationId, RuntimeApplicationAccessDecision>;
	return {
		rolloutId: typeof value.rolloutId === "string" ? value.rolloutId : null,
		rolloutVersion,
		entitlementVersion,
		applicationTokenMode: nativeEntitlementVersion ? "application" : "platform-session",
		apps,
		sitemaps: isRecord(value.sitemaps)
			? (value.sitemaps as Record<string, SitemapNode[]>)
			: {},
	};
}

function normalizeApplicationDecision(
	appName: MfApplicationId,
	value: unknown,
	entitlementVersion: string,
): RuntimeApplicationAccessDecision {
	if (isRecord(value) && typeof value.allowed === "boolean") {
		return {
			appName,
			allowed: value.allowed,
			entitlementVersion,
			reasonCode: value.reasonCode as RuntimeApplicationAccessDecision["reasonCode"],
			release: isRuntimeRelease(value.release, appName) ? value.release : null,
		};
	}
	if (isRuntimeRelease(value, appName)) {
		return { appName, allowed: true, entitlementVersion, release: value };
	}
	return {
		appName,
		allowed: false,
		entitlementVersion,
		reasonCode: "APP_NOT_ASSIGNED",
		release: null,
	};
}

function isRuntimeRelease(
	value: unknown,
	appName: MfApplicationId,
): value is RuntimeApplicationRelease {
	return Boolean(
		isRecord(value) &&
			value.appName === appName &&
			typeof value.releaseId === "string" &&
			typeof value.entry === "string" &&
			typeof value.commitSha === "string",
	);
}

function isRecord(value: unknown): value is Record<string, unknown> {
	return typeof value === "object" && value !== null && !Array.isArray(value);
}

function runtimeContextRevision(context: PlatformRuntimeContext): string {
	return JSON.stringify({
		entitlementVersion: context.entitlementVersion,
		rolloutVersion: context.rolloutVersion,
		apps: Object.fromEntries(
			Object.entries(context.apps).map(([appName, decision]) => [
				appName,
				[decision.allowed, decision.reasonCode, decision.release?.releaseId ?? null],
			]),
		),
	});
}

registerAccessDeniedRecoveryHandler(async ({ request }) => {
	const appName = runtimeApplicationForPath(window.location.pathname);
	const previousVersion = latestContext?.entitlementVersion;
	const context = await loadPlatformRuntimeContext({ force: true });
	const decision = appName ? context.apps[appName] : undefined;
	const method = (request.method ?? 'get').toLowerCase();
	const safeToReplay = ['get', 'head', 'options'].includes(method);
	return {
		retry: Boolean(
			safeToReplay &&
			decision?.allowed &&
			decision.release &&
			previousVersion &&
			previousVersion !== context.entitlementVersion,
		),
	};
});
